/**
 * AI 批量创建付费卡点 · 视图模型派生（纯函数）
 *
 * 与 folderQuota.ts 同约束：故意不 import src/constants/config —— 它引用 webpack
 * 全局 __DESKTOP_DOWNLOAD_URL__，在 Jest 会 ReferenceError 崩纯函数单测。
 * 本地镜像 VIP_KIND 的 4 个值与档名（后端协议固定，与 config 不一致即 bug）。
 */
import { tierCap } from './folderTiers'
import type { TierInfo } from './folderTiers'

/** 镜像 CONFIG.VIP_KIND（src/constants/config.js）· 必须与之同步 */
const VK = { VIP: 1, SVIP: 2, VVIP: 3, EVIP: 4 } as const

/**
 * 档名 · memberOver banner「已超出{档名}上限」用。
 * 实际只有 VIP/VVIP 会走到 memberOver 查表（SVIP/EVIP 在前面已 none）；
 * SVIP/EVIP 列全是为对齐，别误以为多余而删。
 */
const TIER_NAME: Record<number, string> = {
  [VK.VIP]: '基础会员',
  [VK.VVIP]: '高级会员',
  [VK.SVIP]: '超级会员',
  [VK.EVIP]: '企业级会员'
}

export type PaywallKind = 'none' | 'novip' | 'memberOver'

export type PaywallBanner =
  | { kind: 'novip' }
  | { kind: 'memberOver'; newTotal: number; cap: number; tierName: string }

export interface PaywallView {
  kind: PaywallKind
  /** 底部主按钮文案 */
  confirmText: string
  /** banner 渲染数据 · none → null */
  banner: PaywallBanner | null
  /** 升级目标 vip_kind · novip 直接用；memberOver 作 fallback（实际以 422 重算为准） */
  upgradeTargetVipKind: number
  /** 付费来源埋点串 */
  sourceDetail: string
}

/** 接口3 422 配额 body · 仅声明前端用到的字段 */
export interface PaywallQuotaBody {
  reason?: string
  used?: number
  cap?: number
  batch_n?: number
  over_by?: number
  member_kind?: string
  content?: string
}

/**
 * 新增后个数 → 升级目标 vip_kind（PRD §3.2.2）。
 * 触发阈值 = 各档 cap × 90%（接口4 tiers 的 cap 驱动；缺失回落 §3.1 固定值
 * → 20/300/2000 × 0.9 = 18/270/1800，与旧硬编码一致，不回归）。新增后个数落在
 * 某档 90% 以内即定位该档，否则进下一档。
 * 返回值是档位升序 基础<高级<超级<企业，**不是** vip_kind 数值序
 * （vip_kind 非单调：基础1<高级3<超级2<企业4），故 19→VVIP(3) 是对的。
 */
export function batchUpgradeTarget(
  newTotal: number,
  tiers?: TierInfo[]
): number {
  if (newTotal <= tierCap(VK.VIP, tiers) * 0.9) return VK.VIP
  if (newTotal <= tierCap(VK.VVIP, tiers) * 0.9) return VK.VVIP
  if (newTotal <= tierCap(VK.SVIP, tiers) * 0.9) return VK.SVIP
  return VK.EVIP
}

/** 升级目标 vip_kind → 批量创建付费来源 source_detail（企业级无） */
export function batchSourceDetail(targetVipKind: number): string {
  switch (targetVipKind) {
    case VK.VIP:
      return 'groupFolderBatchCreateVip'
    case VK.VVIP:
      return 'groupFolderBatchCreateVvip'
    case VK.SVIP:
      return 'groupFolderBatchCreateSvip'
    default:
      return ''
  }
}

/** 升级目标 vip_kind → 单个创建付费来源 source_detail（PRD §3.2.3，企业级无） */
export function singleCreateSourceDetail(targetVipKind: number): string {
  switch (targetVipKind) {
    case VK.VIP:
      return 'groupFolderCreateVip'
    case VK.VVIP:
      return 'groupFolderCreateVvip'
    case VK.SVIP:
      return 'groupFolderCreateSvip'
    default:
      return ''
  }
}

export interface SingleCreatePaywall {
  /** 是否拦截（拉开通弹窗而非建夹弹窗） */
  blocked: boolean
  /** 升级目标 vip_kind · blocked 时有意义 */
  upgradeTargetVipKind: number
  /** 付费来源埋点 */
  sourceDetail: string
}

/**
 * 单个创建付费卡点（PRD §3.2.3）· 建 1 个文件夹前的额度判定。
 * 超级/企业本期不限（与批量一致）；其它档 newTotal=used+1 超 cap 即拦截。
 * 升级区间与批量同（复用 batchUpgradeTarget），仅 source_detail 不同。
 */
export function deriveSingleCreatePaywall(input: {
  vipKind: number
  used: number
  cap: number
  tiers?: TierInfo[]
}): SingleCreatePaywall {
  const { vipKind, used, cap, tiers } = input
  if (vipKind === VK.SVIP || vipKind === VK.EVIP) {
    return { blocked: false, upgradeTargetVipKind: 0, sourceDetail: '' }
  }
  const newTotal = used + 1
  if (cap > 0 && newTotal > cap) {
    const target = batchUpgradeTarget(newTotal, tiers)
    return {
      blocked: true,
      upgradeTargetVipKind: target,
      sourceDetail: singleCreateSourceDetail(target)
    }
  }
  return { blocked: false, upgradeTargetVipKind: 0, sourceDetail: '' }
}

export function derivePaywallView(input: {
  vipKind: number
  used: number
  cap: number
  selectedCount: number
  tiers?: TierInfo[]
}): PaywallView {
  const { vipKind, used, cap, selectedCount, tiers } = input
  const newTotal = used + selectedCount
  const target = batchUpgradeTarget(newTotal, tiers)
  const sourceDetail = batchSourceDetail(target)

  // 非会员：AI 批量创建已提升为「高级会员」功能（商业化调整 2026-06）。
  // 跳转只定位 高级/超级 两档：新增后个数在高级 cap(90%) 以内 → 高级，否则 → 超级；
  // 不再回落基础档（基础会员仍可用、不在此卡，见需求“基础/高级保持现状”）。
  // 阈值用 tierCap(VVIP)：服务端下调高级 cap（如 100）时前端自动跟随。
  if (vipKind === 0) {
    const novipTarget =
      newTotal <= tierCap(VK.VVIP, tiers) * 0.9 ? VK.VVIP : VK.SVIP
    return {
      kind: 'novip',
      confirmText: '开通会员继续',
      banner: { kind: 'novip' },
      upgradeTargetVipKind: novipTarget,
      sourceDetail: batchSourceDetail(novipTarget)
    }
  }

  // 超级 / 企业：本期不限，不卡
  if (vipKind === VK.SVIP || vipKind === VK.EVIP) {
    return {
      kind: 'none',
      confirmText: '确认创建',
      banner: null,
      upgradeTargetVipKind: target,
      sourceDetail
    }
  }

  // 基础 / 高级：超额才卡（=cap 不算超）
  if (cap > 0 && newTotal > cap) {
    return {
      kind: 'memberOver',
      confirmText: '升级会员继续',
      banner: {
        kind: 'memberOver',
        newTotal,
        cap,
        tierName: TIER_NAME[vipKind] || '会员'
      },
      upgradeTargetVipKind: target,
      sourceDetail
    }
  }

  return {
    kind: 'none',
    confirmText: '确认创建',
    banner: null,
    upgradeTargetVipKind: target,
    sourceDetail
  }
}
